import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@monksflow/monks-ui';
import { LayersIcon, PlusIcon, Settings2Icon, WrenchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import { useSaveToolAccess } from '@/features/admin/model/useSaveToolAccess';
import { useToolsAccessData } from '@/features/admin/model/useToolsAccessData';
import { ManageToolWorkspacesSheet } from '@/features/admin/ui/ManageToolWorkspacesSheet';
import type { ToolRowData, ToolTypeFilter } from '@/features/admin/ui/ToolsTable';
import { getToolSortConfig, ToolsTableV2, type ToolSortId } from '@/features/admin/ui/ToolsTableV2';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { MANAGED_TOOL_IDS } from '@/shared/config/managedTools';
import { ROUTES } from '@/shared/config/routes';
import { useModalState } from '@/shared/hooks/useModalState';
import { FilterChipPopover, SearchInput, SortChipPopover } from '@/shared/ui';

import { AdminPageShell } from '../AdminPageShell';
import { useBuiltAgentRows } from './useBuiltAgentRows';
// Deep imports on purpose: the slice's index also carries the canvas, which only its page loads.
import { isAgentDefinitionAgentId } from '@/features/agent-builder/model/types';
import { useArchiveDefinition } from '@/features/agent-builder/model/useArchiveDefinition';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { HostRequestError } from '@inference.engine/agentic-ui/host-errors';
import { notificationService } from '@/lib/services/notification.service';
import {
  DEFAULT_TYPE_FILTER,
  TYPE_FILTER_OPTIONS,
  filterToolsByType,
  getStoredTypeFilter,
  storeTypeFilter,
} from './Tools.constants';

interface ToolsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  typeFilter: ToolTypeFilter;
  onTypeFilterChange: (value: ToolTypeFilter) => void;
  sortId: ToolSortId | null;
  onSortChange: (value: ToolSortId | null) => void;
  /** Absent while agents cannot be built here. */
  onCreateTool?: () => void;
  /** Whether archived built agents can be listed, which only the builder can restore. */
  offerArchived: boolean;
  /** Whether rows carry a modification date to sort by, which only built agents have. */
  offerLastModified: boolean;
}

/**
 * First row inside the body card: the search, sort and type filter on the left,
 * the one thing this page creates on the right. Built from the same parts as the
 * Knowledge Files toolbar - a 420px large search field and a large action
 * button - minus its padding, since the settings shell already insets the card.
 */
function ToolsToolbar({
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  sortId,
  onSortChange,
  onCreateTool,
  offerArchived,
  offerLastModified,
}: ToolsToolbarProps) {
  const { t } = useTranslation('admin');
  const sortConfig = getToolSortConfig(offerLastModified);

  // "All" is the chip's reset row rather than an option of its own, so the
  // stored value it stands for never reaches the list.
  const typeOptions = useMemo(
    () =>
      TYPE_FILTER_OPTIONS.filter(
        ({ value }) => value !== 'all' && (offerArchived || value !== 'archived'),
      ).map(({ value, labelKey }) => ({
        value,
        label: t(labelKey),
      })),
    [t, offerArchived],
  );

  return (
    <div
      data-testid="tools-toolbar"
      className="flex items-center justify-between gap-md p-section pb-md"
    >
      <div className="flex items-center gap-xs">
        <SearchInput
          value={search}
          onChange={onSearchChange}
          placeholder={t('admin.tools.searchToolsByName')}
          aria-label={t('admin.tools.searchToolsLabel')}
          clearable
          size="default"
          className="w-[420px]"
        />

        <SortChipPopover
          size="md"
          options={sortConfig.choices}
          value={sortId}
          onChange={(next) => {
            onSortChange(sortConfig.toSortId(next));
          }}
          resetOptionLabel={sortConfig.reset.label}
          // The chip shows only an icon, so the order it is set to has to be in its name.
          triggerAriaLabel={t('admin.tools.sortTools', {
            order: sortConfig.resolve(sortId).label,
          })}
        />

        <FilterChipPopover<ToolTypeFilter>
          size="md"
          label={t('admin.toolsTable.type')}
          triggerAriaLabel={t('admin.tools.filterToolsByType')}
          options={typeOptions}
          value={typeFilter === 'all' ? null : typeFilter}
          onChange={(next) => {
            onTypeFilterChange(next ?? 'all');
          }}
          resetOptionLabel={t('admin.tools.all')}
          searchable={false}
        />
      </div>

      {onCreateTool && (
        <Button size="default" onClick={onCreateTool}>
          <PlusIcon />
          {t('admin.tools.createCustomTool')}
        </Button>
      )}
    </div>
  );
}

export function ToolsPage() {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { t: tBuilder } = useTranslation('agentBuilder');

  const navigate = useNavigate();
  const builderOn = useBooleanFlagValue('relay_agent_builder', false);
  const access = useToolsAccessData();
  const { workspaces } = access;
  const built = useBuiltAgentRows(builderOn, workspaces);
  // A built agent is listed from its definition, drafts included, so its published card from
  // `GET /agents` would list it twice. Without the builder that card is all there is, and it is
  // still a built agent: the workspace sheet and configuration write settings it never reads.
  const tools = useMemo(
    () =>
      builderOn
        ? [...access.tools.filter((tool) => !isAgentDefinitionAgentId(tool.id)), ...built.rows]
        : access.tools.map((tool) =>
            isAgentDefinitionAgentId(tool.id) ? { ...tool, builtAgent: {} } : tool,
          ),
    [builderOn, access.tools, built.rows],
  );
  const isLoading = access.isLoading || built.isLoading;
  const isError = access.isError || built.isError;
  const { refetch: refetchAccess } = access;
  const { refetch: refetchBuilt } = built;
  const refetch = () => Promise.all([refetchAccess(), builderOn ? refetchBuilt() : undefined]);
  const { save, isSaving, isError: isSaveError, reset: resetSave } = useSaveToolAccess();
  const { archive, isArchiving: isDeleting } = useArchiveDefinition();

  const modal = useModalState();
  const [selectedTool, setSelectedTool] = useState<ToolRowData | null>(null);

  const deleteModal = useModalState();
  const [deletingTool, setDeletingTool] = useState<ToolRowData | null>(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ToolTypeFilter>(getStoredTypeFilter);
  const [sortId, setSortId] = useState<ToolSortId | null>(null);

  const handleTypeFilterChange = (value: ToolTypeFilter) => {
    storeTypeFilter(value);
    setTypeFilter(value);
  };

  // A stored Archived choice outlives the flag; without the builder there is nothing to list.
  const activeFilter: ToolTypeFilter =
    !builderOn && typeFilter === 'archived' ? DEFAULT_TYPE_FILTER : typeFilter;
  const filteredTools = useMemo(
    () => filterToolsByType(tools, activeFilter),
    [tools, activeFilter],
  );

  // The mutation is reset on both edges of the surface, so a failure reported on one
  // visit cannot still be showing when the next one opens.
  const handleManageWorkspaces = (tool: ToolRowData) => {
    resetSave();
    setSelectedTool(tool);
    modal.open();
  };

  const handleCloseWorkspaces = () => {
    modal.close();
    resetSave();
  };

  const handleDeleteAgent = (tool: ToolRowData) => {
    setDeletingTool(tool);
    deleteModal.open();
  };

  const handleConfirmDelete = () => {
    if (!deletingTool) return;
    archive(deletingTool.id)
      .then(() => {
        deleteModal.close();
        setDeletingTool(null);
        void refetch();
      })
      .catch((error: unknown) => {
        // The builder's requests skip the global error toast, so the reason is told here.
        notificationService.showError(
          error instanceof HostRequestError && error.code === 'definition_has_dependents'
            ? tBuilder('lifecycle.hasDependents')
            : tBuilder('lifecycle.failed'),
        );
      });
  };

  return (
    <AdminPageShell
      title={t('admin.adminLayout.tools')}
      ownsBodyInset
      about={{
        title: t('admin.tools.about.title'),
        description: t('admin.tools.about.description'),
        rows: [
          { icon: LayersIcon, text: t('admin.tools.about.chooseWorkspaces') },
          { icon: Settings2Icon, text: t('admin.tools.about.openToConfigure') },
          // Only the builder creates tools, so the row is offered only where the button is.
          ...(builderOn
            ? [{ icon: WrenchIcon, text: t('admin.tools.about.createCustomTools') }]
            : []),
        ],
      }}
    >
      <div className="flex h-full min-h-0 flex-col">
        <ToolsToolbar
          search={search}
          onSearchChange={setSearch}
          typeFilter={activeFilter}
          onTypeFilterChange={handleTypeFilterChange}
          sortId={sortId}
          onSortChange={setSortId}
          offerArchived={builderOn}
          offerLastModified={builderOn}
          {...(builderOn ? { onCreateTool: () => void navigate(ROUTES.ADMIN_CREATE_AGENT) } : {})}
        />

        <ToolsTableV2
          // Nothing until every source has loaded once, so the skeleton shows instead of a list
          // whose built agents arrive after the rest.
          tools={isLoading ? [] : filteredTools}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          searchQuery={search}
          typeFilter={activeFilter}
          sortId={sortId}
          onSortChange={setSortId}
          showLastModified={builderOn}
          onManageWorkspaces={handleManageWorkspaces}
          // Editing and deleting a custom agent both go through the builder, so without it neither
          // is offered rather than bouncing back to this list.
          {...(builderOn
            ? {
                onEditAgent: (tool: ToolRowData) => void navigate(ROUTES.ADMIN_EDIT_AGENT(tool.id)),
                onDeleteAgent: handleDeleteAgent,
              }
            : {})}
          externalToolsAgentId={SPECIAL_AGENT_IDS.EXTERNAL_TOOLS}
          managedToolIds={MANAGED_TOOL_IDS}
        />
      </div>

      {modal.isMounted && (
        <ManageToolWorkspacesSheet
          open={modal.isOpen}
          onClose={handleCloseWorkspaces}
          tool={selectedTool}
          allWorkspaces={workspaces}
          onSave={save}
          isSaving={isSaving}
          isError={isSaveError}
        />
      )}

      {deleteModal.isMounted && deletingTool && (
        <DeleteConfirmationModal
          open={deleteModal.isOpen}
          isDeleting={isDeleting}
          onCancel={() => {
            deleteModal.close();
            setDeletingTool(null);
          }}
          onConfirmDelete={handleConfirmDelete}
          resourceType={tCommon('components.deleteConfirmationModal.tool')}
          resourceName={deletingTool.name}
          warningMessage={t('admin.tools.theToolWillBeDeactivated')}
        />
      )}
    </AdminPageShell>
  );
}
